import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSessionUser } from "../../../../utils/auth/sessionUser";
import { Button, Tag, Space, Modal, message, Typography, Tooltip } from 'antd';
import { ContrastTag } from '../../../../components/shared';
import {
	PlusOutlined,
	EditOutlined,
	CopyOutlined,
	DeleteOutlined,
	CheckCircleOutlined,
	MinusCircleOutlined,
	LockOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';

import PageContainer from '../../../../components/shared/PageContainer';
import PageToolbar from '../../../../components/shared/PageToolbar';
import { DataTable, StatusTag } from '../../../../components/shared';
import { fetchTemplates, deleteTemplate, duplicateTemplate } from '../../../../api/healthcare/TreatmentPlanApi';
import { TreatmentPlanTemplate } from '../../../../types/healthcare/TreatmentPlanTypes';
import TreatmentPlanBuilderModal from './TreatmentPlanBuilderModal';

const { Text } = Typography;

const TEMPLATE_CATEGORIES = ['All', 'Onboarding', 'Post-Appointment', 'Chronic Care', 'Follow-Up', 'Wellness', 'Mental Health', 'Other'];

const TreatmentPlanTemplatesPage: React.FC = () => {
	const user = getSessionUser();

	const [templates, setTemplates] = useState<TreatmentPlanTemplate[]>([]);
	const [loading, setLoading] = useState(false);
	const [search, setSearch] = useState("");
	const [categoryFilter, setCategoryFilter] = useState("All");

	const [modalOpen, setModalOpen] = useState(false);
	const [selectedTemplate, setSelectedTemplate] = useState<TreatmentPlanTemplate | null>(null);

	const load = async () => {
		setLoading(true);
		try {
			const data = await fetchTemplates();
			setTemplates(data);
		} catch {
			message.error("Failed to load treatment plan templates");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		load();
	}, []);

	const handleReset = () => {
		setSearch("");
		setCategoryFilter("All");
	};

	const handleDelete = (tpl: TreatmentPlanTemplate) => {
		Modal.confirm({
			title: `Delete "${tpl.name}"?`,
			content: "This cannot be undone. Templates with active patient plans cannot be deleted.",
			okText: "Delete",
			okType: "danger",
			onOk: async () => {
				try {
					await deleteTemplate(tpl.id);
					message.success("Template deleted");
					load();
				} catch (err: any) {
					message.error(err?.response?.data?.message ?? "Delete failed");
				}
			},
		});
	};

	const handleDuplicate = async (tpl: TreatmentPlanTemplate) => {
		try {
			const copy = await duplicateTemplate(tpl.id);
			message.success(`"${copy.name}" created`);
			load();
		} catch {
			message.error("Duplicate failed");
		}
	};

	const handleOpenModal = (tpl?: TreatmentPlanTemplate) => {
		setLoading(true);
		if (!tpl) {
			setSelectedTemplate(null);
		} else {
			setSelectedTemplate(tpl);
		}
		setModalOpen(true);
		setLoading(false);
	};

	const handleCloseModal = () => {
		setSelectedTemplate(null);
		setLoading(false);
		setModalOpen(false);
	};




	const filtered = templates.filter((t) => {
		const matchSearch = !search || t.name.toLowerCase().includes(search.toLowerCase());
		const matchCat = categoryFilter === "All" || t.category === categoryFilter;
		return matchSearch && matchCat;
	});

	const columns: ColumnsType<TreatmentPlanTemplate> = [
		{
			title: "Name",
			dataIndex: "name",
			sorter: (a, b) => a.name.localeCompare(b.name),
			render: (name, record) => (
				<Space>
					<Text strong>{name}</Text>
					{record.version > 1 && (
						<ContrastTag background="var(--mg-info)" style={{ fontSize: 11 }}>
							v{record.version}
						</ContrastTag>
					)}
				</Space>
			),
		},
		{
			title: "Category",
			dataIndex: "category",
			width: 160,
			render: (cat) => (cat ? <Tag>{cat}</Tag> : <Text type="secondary">—</Text>),
		},
		{
			title: "Stages",
			width: 90,
			align: "center",
			render: (_, record) => <Text style={{ fontWeight: 600, color: "var(--mg-primary)" }}>{record.stages?.length ?? 0}</Text>,
		},
		{
			title: "Status",
			dataIndex: "is_active",
			width: 110,
			render: (active) => <StatusTag status={active ? "active" : "inactive"} />,
		},
		{
			title: 'Peptide',
			dataIndex: 'is_peptide_plan',
			key: 'is_peptide_plan',
			width: 80,
			align: 'center' as const,
			render: (v: boolean) => v
				? <CheckCircleOutlined style={{ color: 'var(--mg-success)', fontSize: 15 }} />
				: <MinusCircleOutlined style={{ color: 'var(--mg-text-disabled)', fontSize: 15 }} />,
		},
		{
			title: 'Global',
			dataIndex: 'is_global',
			key: 'is_global',
			width: 75,
			align: 'center' as const,
			render: (v: boolean) => v
				? <CheckCircleOutlined style={{ color: 'var(--mg-success)', fontSize: 15 }} />
				: <MinusCircleOutlined style={{ color: 'var(--mg-text-disabled)', fontSize: 15 }} />,
		},
		{
			title: "Updated",
			dataIndex: "updated_at",
			width: 130,
			render: (v) => <Text type="secondary">{dayjs(v).format("D MMM YYYY")}</Text>,
			sorter: (a, b) => new Date(a.updated_at).getTime() - new Date(b.updated_at).getTime(),
			defaultSortOrder: "descend",
		},
		{
			title: "",
			key: "actions",
			width: 110,
			render: (_, record) => {
				const isLocked = record.is_global;
				return (
					<Space size={2}>
						{isLocked ? (
							<Tooltip title="Managed by Xoe Med — read only">
								<Button type="text" size="small" icon={<LockOutlined />} disabled />
							</Tooltip>
						) : (
							<Tooltip title="Edit in builder">
								<Button
									type="text"
									size="small"
									icon={<EditOutlined />}
									onClick={(e) => {
										e.stopPropagation();
										handleOpenModal();
									}}
								/>
							</Tooltip>
						)}
						<Tooltip title="Duplicate">
							<Button
								type="text"
								size="small"
								icon={<CopyOutlined />}
								onClick={(e) => {
									e.stopPropagation();
									handleDuplicate(record);
								}}
							/>
						</Tooltip>
						{!isLocked && (
							<Tooltip title="Delete">
								<Button
									type="text"
									size="small"
									danger
									icon={<DeleteOutlined />}
									onClick={(e) => {
										e.stopPropagation();
										handleDelete(record);
									}}
								/>
							</Tooltip>
						)}
					</Space>
				);
			},
		},
	];

	return (
		<PageContainer data-testid="treatment-plan-templates-root">
			<PageToolbar
				primaryAction={{
					label: "New Template",
					icon: <PlusOutlined />,
					onClick: () => handleOpenModal(null),
					testId: "treatment-plan-templates-new-btn",
					type: "primary",
					danger: false,
					disabled: false,
				}}
				filters={[
					{
						type: "search",
						placeholder: "Search templates…",
						value: search,
						onChange: setSearch,
						width: 260,
					},
					{
						type: "select",
						placeholder: "All categories",
						options: TEMPLATE_CATEGORIES.map((c) => ({ value: c, label: c })),
						value: categoryFilter,
						onChange: setCategoryFilter,
						width: 180,
					},
				]}
				onReset={handleReset}
			/>

			<DataTable
				title="Templates"
				columns={columns}
				dataSource={filtered}
				rowKey="id"
				loading={loading}
				onRowClick={(record) => {
					if (record.is_global) return;
					handleOpenModal(record);
				}}
				emptyTitle="No templates yet"
				emptyDescription="Create your first treatment plan template to get started"
				emptyAction={{ label: "New Template", onClick: () => handleOpenModal(null) }}
			/>

			{modalOpen && (
				<TreatmentPlanBuilderModal
					isOpen={modalOpen}
					template={selectedTemplate}
					onClose={handleCloseModal}
				/>
			)}
		</PageContainer>
	);
};

export default TreatmentPlanTemplatesPage;
